import type { BuildingFamily } from "./buildings.js";
import type { StockpileResource } from "./resources.js";

/**
 * §6.2 production catalog — Phase 1.E + Phase 2.A.
 * Three items each for the only two production-capable families at MVP cut:
 * BARRACKS (infantry stacks) and RESOURCE_FACTORY (stockpile bundles).
 * Phase 2.A added a `reward` discriminator so claim flow can settle either a
 * stockpile bump (Resource Factory) or an inventory increment (Barracks).
 */

/** Discriminator for catalog entries — `building` is the only family that may produce this item. */
export type ProductionBuildingFamily = Extract<BuildingFamily, "BARRACKS" | "RESOURCE_FACTORY">;

export const PRODUCTION_BUILDING_FAMILIES: readonly ProductionBuildingFamily[] = [
  "BARRACKS",
  "RESOURCE_FACTORY",
] as const;

/** Reward delivered when the player claims a completed job. Discriminated. */
export type ProductionReward =
  | { readonly kind: "stockpile"; readonly resource: StockpileResource; readonly amount: number }
  | { readonly kind: "inventory"; readonly itemKey: string; readonly count: number };

export interface ProductionItem {
  /** Stable item key — flows through the wire and into the production_jobs.item_key column. */
  readonly key: string;
  /** Family that may produce this item. The route rejects start requests on a mismatched building. */
  readonly building: ProductionBuildingFamily;
  /** Display name used in the §6.2 catalog list. */
  readonly displayName: string;
  /** Short description — surfaces under the catalog name in two-line layout. */
  readonly summary: string;
  /** Base craft duration in milliseconds. */
  readonly baseDurationMs: number;
  /** Speed-Up shortcut duration (used by the §6.2 Speed-Up button preview). */
  readonly accelDurationMs: number;
  /** Treasury cost per craft (deducted on Start). */
  readonly costTreasury: number;
  /** Reserves cost per craft (only Barracks items consume manpower at MVP). */
  readonly costReserves: number;
  /** What the player receives when claiming a completed job. */
  readonly reward: ProductionReward;
}

export const PRODUCTION_CATALOG: readonly ProductionItem[] = [
  // RESOURCE_FACTORY — stockpile bundles.
  {
    key: "PROVISION_BUNDLE",
    building: "RESOURCE_FACTORY",
    displayName: "Provision bundle",
    summary: "Crates of dried rations and field kit. Sustains 10 squads for one day.",
    baseDurationMs: 30_000,
    accelDurationMs: 12_000,
    costTreasury: 200,
    costReserves: 0,
    reward: { kind: "stockpile", resource: "PROVISIONS", amount: 50_000 },
  },
  {
    key: "TREASURY_CRATE",
    building: "RESOURCE_FACTORY",
    displayName: "Treasury crate",
    summary: "Stamped pay packets and minted coin. Replenishes the player's hire chest.",
    baseDurationMs: 60_000,
    accelDurationMs: 25_000,
    costTreasury: 400,
    costReserves: 0,
    reward: { kind: "stockpile", resource: "TREASURY", amount: 30_000 },
  },
  {
    key: "RESERVE_DRAFT",
    building: "RESOURCE_FACTORY",
    displayName: "Reserve draft",
    summary: "Conscription notices processed and routed to the manpower pool.",
    baseDurationMs: 90_000,
    accelDurationMs: 35_000,
    costTreasury: 600,
    costReserves: 0,
    reward: { kind: "stockpile", resource: "RESERVES", amount: 25_000 },
  },
  // BARRACKS — infantry squads.
  {
    key: "RIFLE_SQUAD",
    building: "BARRACKS",
    displayName: "Rifle squad",
    summary: "Standard rifle infantry. Eight troopers, bolt-action small arms.",
    baseDurationMs: 45_000,
    accelDurationMs: 18_000,
    costTreasury: 350,
    costReserves: 50,
    reward: { kind: "inventory", itemKey: "RIFLE_SQUAD", count: 1 },
  },
  {
    key: "SMG_SQUAD",
    building: "BARRACKS",
    displayName: "SMG squad",
    summary: "Close-range assault troopers. Heavier per-man fire density at short range.",
    baseDurationMs: 75_000,
    accelDurationMs: 30_000,
    costTreasury: 600,
    costReserves: 80,
    reward: { kind: "inventory", itemKey: "SMG_SQUAD", count: 1 },
  },
  {
    key: "MG_TEAM",
    building: "BARRACKS",
    displayName: "MG team",
    summary: "Crew-served machine gun. Long sustained suppression on dug-in positions.",
    baseDurationMs: 120_000,
    accelDurationMs: 45_000,
    costTreasury: 900,
    costReserves: 120,
    reward: { kind: "inventory", itemKey: "MG_TEAM", count: 1 },
  },
];

/** Quick lookup by item key. */
export const PRODUCTION_ITEM_BY_KEY: Readonly<Record<string, ProductionItem>> = Object.fromEntries(
  PRODUCTION_CATALOG.map((it) => [it.key, it]),
);

/** Filtered catalog for the panel of a specific family. */
export function productionCatalogFor(family: ProductionBuildingFamily): readonly ProductionItem[] {
  return PRODUCTION_CATALOG.filter((it) => it.building === family);
}

/**
 * §6.2 workshop slot count grows with building level:
 * Lv 1-4 → 1 slot, Lv 5-9 → 2, Lv 10-19 → 3, Lv 20-31 → 4, Lv 32 → 5.
 *
 * @throws RangeError on level < 1.
 */
export function workshopSlotCount(level: number): number {
  if (level < 1 || !Number.isFinite(level)) {
    throw new RangeError(`level must be ≥ 1, got ${level}`);
  }
  if (level >= 32) return 5;
  if (level >= 20) return 4;
  if (level >= 10) return 3;
  if (level >= 5) return 2;
  return 1;
}

/** §6.2 queue capacity in addition to the active workshop slots. */
export function queueCapacity(level: number): number {
  if (level < 1 || !Number.isFinite(level)) {
    throw new RangeError(`level must be ≥ 1, got ${level}`);
  }
  return Math.max(2, Math.floor(level / 2) + 1);
}
